import { ChangeDetectionStrategy, Component, OnInit, effect, signal } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { ProductCost } from 'src/app/bo/products/product-cost';
import { Site } from 'src/app/bo/site';
import { SiteCost } from 'src/app/bo/SiteCost';
import { Warehouse } from 'src/app/bo/warehouse';
import { Factory } from 'src/app/bo/factory';
import { Currency, CurrencyService } from 'src/app/services/currency.service';
import { FonctionsService } from 'src/app/services/fonctions-partagees.service';
import { SitesService } from 'src/app/services/sites.service';
import { ProductSourcesService } from 'src/app/services/product-sources.service';
import { UtilisateurService } from 'src/app/services/utilisateur.service';
import { NatureType } from 'src/app/bo/products/natureType';
import { ProductType } from 'src/app/bo/products/productType';

@Component({
  selector: 'app-cost',
  templateUrl: './cost.component.html',
  styleUrls: ['./cost.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: true,
  imports: [
    FormsModule,
    TranslateModule,
    NgSelectModule
]
})
export class CostComponent implements OnInit {

  currencies = signal<Currency[]>([]);
  currentSite: Site;
  siteCosts = signal<SiteCost[]>([]);
  currency = signal('');
  productCosts = signal<ProductCost[]>([]);
  warehouses = signal<Warehouse[]>([]);
  factories = signal<Factory[]>([]);

  editingStates = signal<{ [key: string]: boolean }>({});
  productCosts_values = signal<{ [key: string]: number }>({});

  providers: {code: string, name: string}[] = [];
  selectedProvider: string | null = null;

  sectionStates: { [key: string]: boolean } = {
    cartridge: true,
    booster: true,
    bulk: true,
    detcord: true,
    detonator: true
  };

  productSections = [
    { key: 'cartridge', label: 'COST.CARTRIDGE' },
    { key: 'booster', label: 'COST.BOOSTER' },
    { key: 'bulk', label: 'COST.EXPLOSIVE' },
    { key: 'detcord', label: 'COST.DETCORD' },
    { key: 'detonator', label: 'COST.DETONATOR' }
  ];

  // Default GES transport factor: kg CO2 per kg of product per km
  private readonly DEFAULT_GES_TRANSPORT_FACTOR = 0.0001;

  private extractWeightFromFullname(fullname: string): number | null {
    // Match pattern like "/ 3125 g )" or "/ 3125 g)"
    const match = fullname?.match(/\/\s*(\d+)\s*g\s*\)/i);
    return match ? parseInt(match[1], 10) : null;
  }

  calculateGesDetonation(product: ProductCost): number {
    if (!product.gesDetonation) {
      return 0;
    }
    const weightGrams = this.extractWeightFromFullname(product.fullname);
    const weightKg = weightGrams ? weightGrams / 1000 : 1;
    return Math.round(product.gesDetonation * weightKg * 10) / 10;
  }

  calculateGesProduction(product: ProductCost): number {
    if (!product.gesProduction) {
      return 0;
    }
    const weightGrams = this.extractWeightFromFullname(product.fullname);
    const weightKg = weightGrams ? weightGrams / 1000 : 1;
    return Math.round(product.gesProduction * weightKg * 10) / 10;
  }

  constructor(
    private readonly currencyService: CurrencyService,
    private readonly siteService: SitesService,
    private readonly productSourcesService: ProductSourcesService,
    private readonly fonctionsService: FonctionsService,
    private readonly translateService: TranslateService,
    private readonly utilisateurService: UtilisateurService
  ) {
    this.getCurrencies();
    // Reload all site-bound data whenever the current site changes
    effect(() => {
      const site = this.utilisateurService.currentSite();
      if (site?.siteId) {
        this.loadDataForSite(site);
      }
    });
  }

  ngOnInit(): void {
  }

  private loadDataForSite(site: Site): void {
    this.currentSite = site;
    // Reset signals to avoid showing stale data from the previous site
    this.siteCosts.set([]);
    this.productCosts.set([]);
    this.editingStates.set({});
    this.productCosts_values.set({});
    this.providers = [];
    this.selectedProvider = 'all';

    this.loadWarehouses();
    this.siteService.getSiteCosts(site.siteId).subscribe({
      next: (siteCosts) => {
        this.siteCosts.set(siteCosts);

        if (this.siteCosts() && this.siteCosts().length > 0) {
          this.currency.set(this.siteCosts()[0].currency);
        }
      },
      error: (err) => console.error('Erreur chargement des coûts', err)
    });
    this.siteService.getProductCosts(site.regionId, site.siteId).subscribe({
      next: (data) => {
        this.productCosts.set(data);
        this.extractProviders();
        if (!this.currency() && data.length > 0 && data[0].currency) {
          this.currency.set(data[0].currency);
        }
        // Load CO2 cost data for each product
        this.loadProductsCo2Costs();
      },
      error: (err) => console.error('Erreur chargement des coûts de forage', err)
    })
  }

  loadWarehouses(): void {
    // Try multiple sources to get orgId
    const orgId = this.currentSite?.orgId
      || this.currentSite?.region?.orgId
      || this.utilisateurService.getCurrentRegion()?.orgId;

    if (!orgId) {
      console.warn('No orgId found for current site:', this.currentSite);
      return;
    }
    this.productSourcesService.getWarehouses(orgId).subscribe({
      next: (warehouses) => {
        this.warehouses.set(warehouses);
      },
      error: (err) => console.error('Erreur chargement des entrepôts', err)
    });
    this.productSourcesService.getFactories(orgId).subscribe({
      next: (factories) => {
        this.factories.set(factories);
      },
      error: (err) => console.error('Erreur chargement des usines', err)
    });
  }

  private loadProductsCo2Costs(): void {
    if (!this.productCosts() || this.productCosts().length === 0 || !this.currentSite?.siteId) {
      return;
    }

    // Get CO2 costs for this site
    this.siteService.getCo2CostsBySite(this.currentSite.siteId).subscribe({
      next: (co2Data) => {
        // Create a map for quick lookup by productId
        const co2Map = new Map(co2Data.map(c => [c.productId, c]));

        // Merge CO2 data into products
        const updated = this.productCosts().map(product => {
          const co2 = co2Map.get(product.productId);
          if (co2) {
            return { ...product, co2CostId: co2.co2CostId, warehouseId: co2.warehouseId };
          }
          return product;
        });
        this.productCosts.set(updated);
      },
      error: (err) => console.error('Erreur chargement des données CO2', err)
    });
  }

  saveSiteCost(siteCost: SiteCost): void {
    const updatedSiteCost = new SiteCost();
    updatedSiteCost.currency = this.currency();
    updatedSiteCost.unitPrice = siteCost.unitPrice;
    updatedSiteCost.costId = siteCost.costId;

    this.siteService.updateSiteCost(this.currentSite.siteId, [updatedSiteCost]).subscribe({
      next: (response) => {
        this.fonctionsService.launch_toast(true);
      },
      error: (err) => {
        this.fonctionsService.launch_toast(false);
      }
    })
  }

  onSiteCostBlur(siteCost: SiteCost): void {
    this.saveSiteCost(siteCost);
  }
  getCurrencies(): void {
    this.currencyService.getCurrencies().subscribe({
      next: (data) => this.currencies.set(data),
      error: (err) => console.error('Erreur chargement des devises', err)
    });
  }

  extractProviders(): void {
    if (!this.productCosts()) {
      return;
    }
    
    const providerMap = new Map<string, {code: string, name: string}>();
    
    providerMap.set('all', { code: 'all', name: this.translateService.instant('COST.ALL_PROVIDERS') });
    
    this.productCosts().forEach(product => {
      if (product.providerCode && product.providerName) {
        if (!providerMap.has(product.providerCode)) {
          providerMap.set(product.providerCode, {
            code: product.providerCode,
            name: product.providerName
          });
        }
      }
    });
    
    this.providers = Array.from(providerMap.values());
    this.selectedProvider = 'all';
  }

  selectProvider(providerCode: string): void {
    this.selectedProvider = providerCode;
  }

  toggleSection(section: string): void {
    this.sectionStates[section] = !this.sectionStates[section];
  }

  getProductsBySection(sectionKey: string): ProductCost[] {
    switch (sectionKey) {
      case 'cartridge':
        return this.getCartridgeProducts();
      case 'booster':
        return this.getBoosterProducts();
      case 'bulk':
        return this.getBulkProducts();
      case 'detcord':
        return this.getDetCordProducts();
      case 'detonator':
        return this.getDetonatorProducts();
      default:
        return [];
    }
  }

  getFilteredProducts(products: ProductCost[]): ProductCost[] {
    if (!this.selectedProvider || this.selectedProvider === 'all') {
      return products;
    }
    
    return products.filter(product => product.providerCode === this.selectedProvider);
  }

  getCartridgeProducts(): ProductCost[] {
    if (!this.productCosts()) {
      return [];
    }
    
    const products = this.productCosts().filter(product => 
      product.productType === ProductType.Cartridge && product.natureType !== NatureType.Booster
    );
    
    return this.getFilteredProducts(products);
  }

  getBoosterProducts(): ProductCost[] {
    if (!this.productCosts()) {
      return [];
    }
    
    const products = this.productCosts().filter(product => 
      product.productType === ProductType.Cartridge && product.natureType === NatureType.Booster
    );
    
    return this.getFilteredProducts(products);
  }

  getBulkProducts(): ProductCost[] {
    if (!this.productCosts()) {
      return [];
    }
    
    const products = this.productCosts().filter(product => 
      product.productType === ProductType.Bulk
    );
    
    return this.getFilteredProducts(products);
  }

  getDetCordProducts(): ProductCost[] {
    if (!this.productCosts()) {
      return [];
    }
    
    const products = this.productCosts().filter(product => 
      product.productType === ProductType.DetCord
    );
    
    return this.getFilteredProducts(products);
  }

  getDetonatorProducts(): ProductCost[] {
    if (!this.productCosts()) {
      return [];
    }
    
    const products = this.productCosts().filter(product => 
      product.productType === ProductType.Detonator
    );
    
    return this.getFilteredProducts(products);
  }

  getMaxRowsArray(): number[] {
    const cartridgesCount = this.getCartridgeProducts().length;
    const boostersCount = this.getBoosterProducts().length;
    const bulksCount = this.getBulkProducts().length;
    const maxRows = Math.max(cartridgesCount, boostersCount, bulksCount);
    
    return Array.from({ length: maxRows }, (_, i) => i);
  }

  stopEditing(type: string, index: number): void {
    const key = `${type}_${index}`;
    this.editingStates.update(states => ({ ...states, [key]: false }));
  }

  isEditing(type: string, index: number): boolean {
    const key = `${type}_${index}`;
    return this.editingStates()[key] || false;
  }

  getProductCostValue(type: string, index: number): number {
    const key = `${type}_${index}`;

    if (this.isEditing(type, index) && this.productCosts_values()[key] !== undefined) {
      return this.productCosts_values()[key];
    }
    let product: ProductCost = null;

    switch (type) {
      case 'cartridge':
        product = this.getCartridgeProducts()[index];
        break;
      case 'booster':
        product = this.getBoosterProducts()[index];
        break;
      case 'bulk':
        product = this.getBulkProducts()[index];
        break;
      case 'detcord':
        product = this.getDetCordProducts()[index];
        break;
      case 'detonator':
        product = this.getDetonatorProducts()[index];
        break;
    }
    return product?.unitPrice || 0;
  }

  setProductCostValue(type: string, index: number, value: string | number): void {
    const key = `${type}_${index}`;
    const parsed = typeof value === 'string' ? parseFloat(value) || 0 : value;
    this.productCosts_values.update(v => ({ ...v, [key]: parsed }));
  }

  startEditing(type: string, index: number): void {
    const key = `${type}_${index}`;

    if (this.productCosts_values()[key] === undefined) {
      let product: ProductCost = null;

      switch (type) {
        case 'cartridge':
          product = this.getCartridgeProducts()[index];
          break;
        case 'booster':
          product = this.getBoosterProducts()[index];
          break;
        case 'bulk':
          product = this.getBulkProducts()[index];
          break;
        case 'detcord':
          product = this.getDetCordProducts()[index];
          break;
        case 'detonator':
          product = this.getDetonatorProducts()[index];
          break;
      }

      const initialValue = product?.unitPrice || 0;
      this.productCosts_values.update(v => ({ ...v, [key]: initialValue }));
    }

    this.editingStates.update(states => ({ ...states, [key]: true }));
  }

  saveProductCost(type: string, index: number): void {
    let product: ProductCost = null;
    
    switch (type) {
      case 'cartridge':
        product = this.getCartridgeProducts()[index];
        break;
      case 'booster':
        product = this.getBoosterProducts()[index];
        break;
      case 'bulk':
        product = this.getBulkProducts()[index];
        break;
      case 'detcord':
        product = this.getDetCordProducts()[index];
        break;
      case 'detonator':
        product = this.getDetonatorProducts()[index];
        break;
    }

    if (product) {
      const updatedProduct: ProductCost = {
        ...product,
        unitPrice: this.getProductCostValue(type, index),
        currency: this.currency()
      };

      this.siteService.updateProductCost(updatedProduct).subscribe({
        next: (response) => {
          this.fonctionsService.launch_toast(true);
          // Immutable update so the productCosts signal notifies the view.
          this.productCosts.update(arr => arr.map(p =>
            p.productId === product.productId
              ? { ...p, unitPrice: updatedProduct.unitPrice, currency: updatedProduct.currency }
              : p
          ));
          this.stopEditing(type, index);
        },
        error: (err) => {
          console.error('Error updating product cost:', err);
          this.fonctionsService.launch_toast(false);
          this.stopEditing(type, index);
        }
      });
    } else {
      this.stopEditing(type, index);
    }
  }

  onInputBlur(type: string, index: number): void {
    this.saveProductCost(type, index);
  }

  calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    if (!lat1 || !lon1 || !lat2 || !lon2) {
      return 0;
    }

    const R = 6371; // Earth's radius in km
    const dLat = this.toRad(lat2 - lat1);
    const dLon = this.toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private toRad(deg: number): number {
    return deg * (Math.PI / 180);
  }

  calculateGesTransport(product: ProductCost): number {
    // GES Transport = (distance factory→warehouse + distance warehouse→site) × co2perKm
    if (!product.warehouseId) {
      return 0;
    }

    const warehouse = this.warehouses().find(w => w.warehouseId === product.warehouseId);
    if (!warehouse || !warehouse.latitude || !warehouse.longitude || !warehouse.factoryId) {
      return 0;
    }

    const factory = this.factories().find(f => f.factoryId === warehouse.factoryId);
    if (!factory || !factory.latitude || !factory.longitude) {
      return 0;
    }

    // Distance factory → warehouse
    const distanceFactoryToWarehouse = this.calculateDistance(
      factory.latitude,
      factory.longitude,
      warehouse.latitude,
      warehouse.longitude
    );

    // Distance warehouse → site
    let distanceWarehouseToSite = 0;
    if (this.currentSite?.coordY && this.currentSite?.coordX) {
      distanceWarehouseToSite = this.calculateDistance(
        warehouse.latitude,
        warehouse.longitude,
        this.currentSite.coordY,
        this.currentSite.coordX
      );
    }

    const totalDistance = distanceFactoryToWarehouse + distanceWarehouseToSite;
    const factor = warehouse.co2perKm ?? this.DEFAULT_GES_TRANSPORT_FACTOR;
    return Math.round(totalDistance * factor * 10) / 10;
  }

  calculateTotalGes(product: ProductCost): number {
    const gesDetonation = this.calculateGesDetonation(product);
    const gesProduction = this.calculateGesProduction(product);
    const gesTransport = this.calculateGesTransport(product);

    return Math.round((gesDetonation + gesProduction + gesTransport) * 10) / 10;
  }

  onWarehouseChange(product: ProductCost, warehouseId: number): void {
    if (!this.currentSite?.siteId) {
      console.error('Cannot update warehouse: currentSite is not defined.');
      return;
    }

    const co2CostData = {
      productId: product.productId,
      siteId: this.currentSite.siteId,
      warehouseId: warehouseId
    };

    const saveOperation = product.co2CostId
      ? this.siteService.updateCo2Cost(product.co2CostId, co2CostData)
      : this.siteService.createCo2Cost(co2CostData);

    saveOperation.subscribe({
      next: (response) => {
        const updated = [...this.productCosts()];
        const productIndex = updated.findIndex(p => p.productId === product.productId);
        if (productIndex !== -1) {
          updated[productIndex] = { ...updated[productIndex], warehouseId: warehouseId };
          if (!updated[productIndex].co2CostId && response?.co2CostId) {
            updated[productIndex] = { ...updated[productIndex], co2CostId: response.co2CostId };
          }
          this.productCosts.set(updated);
        }
        this.fonctionsService.launch_toast(true);
      },
      error: (err) => {
        console.error('Error updating warehouse:', err);
        this.fonctionsService.launch_toast(false);
      }
    });
  }

}
